import { shallowEqual, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import {
  GridCellParams,
  GridColumnHeaderParams,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridRowData,
  GridRowParams,
  LicenseInfo,
  XGrid,
} from '@material-ui/x-grid';
import { makeStyles } from '@material-ui/core/styles';
import { Checkbox } from '@material-ui/core';

import {
  getEntitiesAction,
  setColumns,
  setFilters,
  setPagination,
  setQueryBuilderResult,
} from 'src/redux/actions/querybuilderActions';

import {
  ENTITIES,
  GridColumDefinition,
  GridColumns,
  QuerbuilderPayloadColumns,
  QuerbuilderPayloadPagination,
  QuerybuilderOrders,
  QuerybuilderResult,
  QuerybuilderResultValue,
} from 'src/models/QuerybuilderModel';
import { Store, useAppDispatch } from 'src/redux/Store';
import DatagridHeaderCell from 'src/components/Datagrid/DatagridHeaderCell';
import DatagridCell from 'src/components/Datagrid/DatagridCell';
import DatagridOverlay from 'src/components/Datagrid/DatagridOverlay';
import { resetSelections, setSelections } from 'src/redux/actions/selectionActions';
import { resetCurrentEntity } from 'src/redux/actions/currentEntityActions';
import { updateInvoiceAction } from 'src/redux/actions/invoiceActions';
import { updatePaymentAction } from 'src/redux/actions/paymentActions';
import { PRESET_TYPES } from 'src/models/PresetModel';
import { setSelectedPresets } from 'src/redux/actions/selectedPresetActions';
import {
  denormalizeFilters,
  getOperatorAndFilters,
  kebabToSnake,
  normalizeForForm,
} from 'src/lib/QueryBuilderHelper';
import { Filter, Filters } from 'src/models/AdvanceSearchModel';
import { FILTER_POSITION } from 'src/constants/FilterPosition';
import { setAdvancedSearch } from 'src/redux/actions/advancedSearchAction';
import { getOrganisationBankAccountAction } from 'src/redux/actions/paymentInfoAction';
import {
  getAccountsAction,
  getDepartmentsAction,
  getProgramsAction,
} from 'src/redux/actions/dataActions';
import { addFundraisingTransaction } from 'src/redux/actions/FundraisingTransactionAction';
import { addFundraising } from 'src/redux/actions/fundraisingAction';
import { openModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';

LicenseInfo.setLicenseKey(
  '75a5b9338b8a9b4b41e8bc3516c8986aT1JERVI6Mjg1NzksRVhQSVJZPTE2NjEzNjMyMzYwMDAsS0VZVkVSU0lPTj0x',
);

interface Props {
  entity: ENTITIES;
  dontReloadEntities?: boolean;
}

const useStyles = makeStyles(() => ({
  datagridContainer: {
    height: '600px',
    position: 'relative',
    overflowX: 'hidden',
    marginTop: '16px',
  },
  datagridCell: {
    padding: '0px !important',
    '&:focus': {
      outline: 'none !important',
    },
    '&:focus-within': {
      outline: 'none !important',
    },
  },
  datagridHeaderCell: {
    backgroundColor: '#f8f8f8',
    width: '100% !important',
    padding: '0 !important',
    '&:focus': {
      outline: 'none !important',
    },
    '&:focus-within': {
      outline: 'none !important',
    },
  },
}));

const Datagrid = (props: Props) => {
  const classes = useStyles();

  const {
    entity, dontReloadEntities,
  } = props;

  const organisation = useSelector((state: Store) => state.currentOrganisation, shallowEqual);
  const customfields = useSelector((state: Store) => state.customfields);

  const columns = useSelector((state: Store) => {
    const entityData = state.querybuilder[entity];
    if (entityData && entityData.columns) {
      return entityData.columns.filter((column) => !column.hide_in_datagrid);
    }
    return [] as GridColumns;
  });

  // GET DEFAULT FILTERS
  const { defaultFilters, defaultFilterId } = useSelector(
    (state: Store) => {
      const result = { defaultFilters: null, defaultFilterId: 0 };
      const type = PRESET_TYPES.filter;
      if (!state.presets[entity]) return result;
      if (!state.presets[entity][type]) return result;
      const defaultPreset = state.presets[entity][type].find((preset) => preset.is_default);
      if (defaultPreset) {
        return {
          defaultFilters: defaultPreset.filter,
          defaultFilterId: defaultPreset.id,
        };
      }
      return result;
    },
  );

  const dispatch = useAppDispatch();

  const purgeCustomfields = (filters: Filter[]) => filters.filter((filter: Filter) => {
    const [filterEntity, field] = filter[FILTER_POSITION.field].split('.');
    if (filterEntity === ENTITIES.customfields) {
      return Object.keys(customfields).indexOf(field) !== -1;
    }
    return true;
  });

  useEffect(() => {
    if (dontReloadEntities) return;
    dispatch(resetSelections({ entity }));
    dispatch(resetCurrentEntity());
    dispatch(setQueryBuilderResult({
      entity,
      result: {
        ids: [] as number[],
        count: 0,
        list: [],
      } as QuerybuilderResult,
    }));
    dispatch(setPagination({
      entity,
      pagination: { size: 50, page: 1 },
    } as QuerbuilderPayloadPagination));
    if (!dontReloadEntities) {
      dispatch(setFilters({
        entity,
        filters: null,
      }));
    }
    if (defaultFilterId) {
      const [operator, filterList] = getOperatorAndFilters(defaultFilters as Filters);
      const purgedFilters = purgeCustomfields(filterList);
      dispatch(setAdvancedSearch({
        entity,
        filters: {
          [operator]: purgedFilters,
        },
      }));
      dispatch(setFilters({
        entity,
        filters: {
          [operator]: denormalizeFilters([...purgedFilters]),
        },
      }));
      dispatch(setSelectedPresets({
        entity,
        type: PRESET_TYPES.filter,
        id: defaultFilterId,
      }));
    }
  }, []);

  useEffect(() => {
    if (!dontReloadEntities) {
      dispatch(getAccountsAction());
      dispatch(getDepartmentsAction());
      dispatch(getProgramsAction());
      dispatch(getOrganisationBankAccountAction());
      dispatch(getEntitiesAction(entity));
    }
  }, [organisation]);

  const data = useSelector(
    (state: Store) => (state.querybuilder[entity] && { ...state.querybuilder[entity].result })
      || {} as QuerybuilderResult,
  );

  const pagination = useSelector(
    (state: Store) => (state.querybuilder[entity] && state.querybuilder[entity].pagination)
      || { size: 50, page: 1 },
  );

  const selections = useSelector((state: Store) => {
    if (!!state.selected[entity]) {
      return [...state.selected[entity]];
    }
    return [];
  });

  const order:QuerybuilderOrders = useSelector(
    (state: Store) => (state.querybuilder[entity] && state.querybuilder[entity].order)
      || [] as QuerybuilderOrders,
  );

  const [pageNumber, setPageNumber] = useState(pagination.page);

  const [pageSize, setPageSize] = useState(pagination.size);

  const lastCheckedId = useRef<number | null>(null);

  const handleEntityDispatch = (row: GridRowData) => {
    switch (entity) {
      case ENTITIES.invoices: {
        const invoice = normalizeForForm(row, entity);
        dispatch(updateInvoiceAction({ id: invoice.id, invoice }));
        break;
      }
      case ENTITIES.payments: {
        const payment = normalizeForForm(row, entity);
        dispatch(updatePaymentAction({ id: payment.id, payment }));
        break;
      }
      case ENTITIES.fundraisings: {
        const fundraising = normalizeForForm(row, entity);
        dispatch(addFundraising({ id: fundraising.id, fundraising }));
        break;
      }
      case ENTITIES.fundraisingTransactions: {
        const fundraisingTransaction = normalizeForForm(row, entity);
        dispatch(addFundraisingTransaction(
          { id: fundraisingTransaction.id, fundraisingTransaction },
        ));
        break;
      }
      default:
    }
  };

  const handleOnRowClick = (params: GridRowParams) => {
    handleEntityDispatch(params.row);
    dispatch(setSelections({
      entity,
      selections: [params.id as number],
    }));
  };

  const handleOnRowDoubleClick = (params: GridRowParams) => {
    handleEntityDispatch(params.row);
    dispatch(setSelections({
      entity,
      selections: [params.id as number],
    }));
    switch (entity) {
      case ENTITIES.invoices:
        dispatch(openModal({ modal: MODALS.editInvoice }));
        break;
      case ENTITIES.contacts:
        dispatch(openModal({ modal: MODALS.editContacts }));
        break;
      case ENTITIES.payments:
        dispatch(openModal({ modal: MODALS.editPayment }));
        break;
      case ENTITIES.fundraisingTransactions:
        dispatch(openModal({
          modal: MODALS.editTransaction,
          payload: {
            transactionId: params.id as number,
            fundraisingId: params.row[`${kebabToSnake(entity)}.event_id`],
          },
        }));
        break;
      case ENTITIES.fundraisings:
        dispatch(openModal({ modal: MODALS.editFundraising }));
        break;
      default:
    }
  };

  const getRows = () => {
    const rows:GridRowData[] = [];
    if (!data.list) {
      return rows;
    }
    data.list.forEach((row: QuerybuilderResultValue) => {
      rows.push({
        ...row,
        id: row[`${kebabToSnake(entity)}.id`],
      });
    });
    return rows;
  };

  const getColumns = () => {
    const columnsDefinition:GridColumns = [];

    columnsDefinition.push({
      field: 'selectionBox',
      headerName: '',
      width: 50,
      minWidth: 50,
      sortable: false,
      resizable: false,
      renderHeader: () => (
        <Checkbox
          checked={selections.length > 0}
          onChange={() => dispatch(setSelections({ entity, selections: [] }))} />
      ),
      renderCell: (params: GridCellParams) => (
        <Checkbox
          checked={selections.includes(params.id as number)}
          onChange={() => {}}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            const rowId = params.id as number;
            const currentRows = getRows();

            if (e.shiftKey && lastCheckedId.current !== null) {
              const anchorIndex = currentRows.findIndex((row) => row.id === lastCheckedId.current);
              const currentIndex = currentRows.findIndex((row) => row.id === rowId);
              if (anchorIndex !== -1) {
                const start = Math.min(anchorIndex, currentIndex);
                const end = Math.max(anchorIndex, currentIndex);
                const rangeRows = currentRows.slice(start, end + 1);
                rangeRows.forEach((row) => handleEntityDispatch(row));
                const rangeIds = rangeRows.map((row) => row.id as number);
                const newSelections = [...new Set([...selections, ...rangeIds])];
                dispatch(setSelections({ entity, selections: newSelections }));
                return;
              }
            }

            handleEntityDispatch(params.row);
            const currentIndex = selections.indexOf(rowId);
            const newSelections = [...selections];
            if (currentIndex >= 0) {
              newSelections.splice(currentIndex, 1);
            } else {
              newSelections.push(rowId);
            }
            dispatch(setSelections({ entity, selections: newSelections }));
            lastCheckedId.current = rowId;
          }} />
      ),
      cellClassName: classes.datagridCell,
      headerClassName: classes.datagridHeaderCell,
    });

    if (!columns) {
      return columnsDefinition;
    }
    columns.forEach((column: GridColumDefinition) => {
      columnsDefinition.push({
        ...column,
        renderHeader: (params: GridColumnHeaderParams) => (
          <DatagridHeaderCell params={params} order={order} entity={entity} />
        ),
        renderCell: (params: GridCellParams) => (
          <DatagridCell params={params} />
        ),
        cellClassName: classes.datagridCell,
        headerClassName: classes.datagridHeaderCell,
        sortable: false,
      });
    });
    return columnsDefinition;
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const {
      field, width, minWidth, hide, sortable, resizable,
    } = params.colDef;

    if (field === 'selectionBox') return;

    const newColumns = [
      ...columns,
      {
        field,
        width,
        minWidth,
        hide,
        sortable,
        resizable,
      },
    ];
    dispatch(setColumns({
      entity,
      columns: newColumns,
    } as QuerbuilderPayloadColumns));
  };

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const { targetIndex, oldIndex } = params;
    const dataOldIndex = oldIndex - 1;
    const dataTargetIndex = targetIndex - 1;
    if (dataOldIndex < 0 || dataTargetIndex < 0) return;
    const columnToMove = columns[dataOldIndex];
    columns.splice(dataOldIndex, 1);
    columns.splice(dataTargetIndex, 0, columnToMove);
    dispatch(setColumns({
      entity,
      columns,
    } as QuerbuilderPayloadColumns));
  };

  const handlePageSizeChange = (size:number) => {
    dispatch(setPagination({
      entity,
      pagination: { size, page: pageNumber },
    } as QuerbuilderPayloadPagination));
    setPageSize(size);
    dispatch(getEntitiesAction(entity));
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setPagination({
      entity,
      pagination: { size: pageSize, page: newPage + 1 },
    } as QuerbuilderPayloadPagination));
    setPageNumber(newPage + 1);
    dispatch(getEntitiesAction(entity));
  };

  return (
    <>
      <div className={classes.datagridContainer}>
        <XGrid
          disableColumnMenu
          rows={getRows()}
          columns={getColumns()}
          onColumnWidthChange={handleColumnWidthChange}
          onColumnOrderChange={handleColumnOrderChange}
          paginationMode="server"
          rowCount={data.count}
          rowHeight={36}
          headerHeight={36}
          pagination
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
          onRowClick={handleOnRowClick}
          onRowDoubleClick={handleOnRowDoubleClick}
          selectionModel={selections} />
        <DatagridOverlay />
      </div>
    </>
  );
};

export default Datagrid;
